import type { UIStateAPI } from "@/types";
import { zZoomValue, zZoomMode, zStagePosition } from "@/data/schemas";

interface ViewportAdapterDeps {
  readonly store: any;
  readonly uiState: UIStateAPI;
  readonly eventBus: any;
  readonly getPath: (key: string) => string | null;
  readonly persist: () => void;
}

export const hydrateViewportFromSession = (
  deps: Pick<ViewportAdapterDeps, "store" | "getPath">,
  screen: Record<string, unknown> | null | undefined
): void => {
  if (!screen) return;
  const { store, getPath } = deps;
  const zvPath = getPath("zoomValue");
  const zmPath = getPath("zoomMode");
  const posPath = getPath("stagePosition");
  try {
    if (zvPath) store.registerSchema(zvPath, zZoomValue);
  } catch {}
  try {
    if (zmPath) store.registerSchema(zmPath, zZoomMode);
  } catch {}
  try {
    if (posPath) store.registerSchema(posPath, zStagePosition);
  } catch {}

  try {
    if (typeof (screen as any).zoomValue === "number" && zvPath) {
      store.set(zvPath, (screen as any).zoomValue as any, {
        source: "hydration",
      });
    }
  } catch {}
  try {
    if (typeof (screen as any).zoomMode === "string" && zmPath) {
      store.set(zmPath, (screen as any).zoomMode as any, {
        source: "hydration",
      });
    }
  } catch {}
  try {
    if ((screen as any).stagePosition && posPath) {
      store.set(posPath, (screen as any).stagePosition as any, {
        source: "hydration",
      });
    }
  } catch {}
};

export const registerViewportAdapter = (
  deps: ViewportAdapterDeps
): (() => void) => {
  const { store, uiState, eventBus, getPath, persist } = deps;
  const unsubscribes: (() => void)[] = [];
  
  // Track if we're currently applying a store→UI update to prevent loops
  let applyingStoreUpdate = false;
  // Suppress persisting during active zoom/pan; flush once on end
  let suppressPersist = false;
  let pendingPersist = false;

  // Debug toggles
  const readDebugEnabled = (): boolean => {
    try {
      const d =
        (globalThis as any)?.localStorage?.getItem?.("debug.adapters") ||
        (globalThis as any)?.sessionStorage?.getItem?.("debug.adapters") ||
        (globalThis as any)?.__debugAdapters ||
        "";
      if (typeof d !== "string") return !!(d as any)?.viewport || d === "*";
      const parts = d.split(",").map((s: string) => s.trim());
      return parts.includes("*") || parts.includes("viewport");
    } catch {
      return false;
    }
  };
  let debugEnabled = readDebugEnabled();
  const debug = (...args: unknown[]) => {
    if (debugEnabled) {
      try {
        // eslint-disable-next-line no-console
        console.debug("[adapter:viewport]", ...args);
      } catch {}
    }
  };
  try {
    const unsubDbg = eventBus.on(
      "debug:adapter",
      (payload: { name?: string; enabled?: boolean } | undefined) => {
        try {
          if (!payload) return;
          if (payload.name === "viewport") debugEnabled = !!payload.enabled;
          if (payload.name === "*" && typeof payload.enabled === "boolean")
            debugEnabled = !!payload.enabled;
        } catch {}
      }
    );
    unsubscribes.push(unsubDbg);
  } catch {}

  // Store -> UIState: zoom value/mode and stage position
  const unsubscribeStoreViewport = store.subscribe(
    (path: string) =>
      path.endsWith(".screenState.zoomValue") ||
      path.endsWith(".screenState.zoomMode") ||
      path.endsWith(".screenState.stagePosition"),
    ({
      path,
      value,
      source,
    }: {
      path: string;
      value: unknown;
      source?: string;
    }) => {
      const isHydration = source === "hydration";
      try {
        applyingStoreUpdate = true;
        if (path.endsWith("zoomValue") && typeof value === "number") {
          const mode = (uiState as any).getState?.().zoom_mode || "custom";
          (uiState as any).setZoom(value as any, mode);
          debug("store→UI zoomValue", { value, source });
        } else if (path.endsWith("zoomMode") && typeof value === "string") {
          (uiState as any).setZoomMode(value as any);
          debug("store→UI zoomMode", { value, source });
        } else if (
          path.endsWith("stagePosition") &&
          value &&
          typeof (value as any).x === "number" &&
          typeof (value as any).y === "number"
        ) {
          (uiState as any).setStagePosition((value as any).x, (value as any).y);
          debug("store→UI stagePosition", { value, source });
        }
      } catch {}
      finally {
        applyingStoreUpdate = false;
      }
      if (!isHydration && source !== "socket") {
        if (suppressPersist) pendingPersist = true;
        else persist();
      }
    },
    { debounceMs: 50 }
  );
  unsubscribes.push(unsubscribeStoreViewport);

  // UIState -> Store mapping for viewport (zoom/mode/position)
  const unsubscribeUIStateChanged = eventBus.on(
    "ui-state-changed",
    (data: any) => {
      // Skip if we're currently applying a store update (prevent loop)
      if (applyingStoreUpdate) return;
      
      try {
        const zoomValue = data?.state?.zoom_value;
        const zoomMode = data?.state?.zoom_mode;
        const pos = data?.state?.stage_position;
        const zvPath = getPath("zoomValue");
        const zmPath = getPath("zoomMode");
        const posPath = getPath("stagePosition");
        if (typeof zoomValue === "number" && zvPath) {
          try {
            store.registerSchema(zvPath, zZoomValue);
          } catch {}
          debug("UI→store zoomValue", { zoomValue });
          store.set(zvPath, zoomValue, { source: "viewportAdapter" });
        }
        if (zoomMode && zmPath) {
          try {
            store.registerSchema(zmPath, zZoomMode);
          } catch {}
          debug("UI→store zoomMode", { zoomMode });
          store.set(zmPath, zoomMode, { source: "viewportAdapter" });
        }
        if (
          pos &&
          typeof pos.x === "number" &&
          typeof pos.y === "number" &&
          posPath
        ) {
          try {
            store.registerSchema(posPath, zStagePosition);
          } catch {}
          debug("UI→store stagePosition", { pos });
          store.set(
            posPath,
            { x: pos.x, y: pos.y },
            {
              source: "viewportAdapter",
            }
          );
        }
      } catch {}
    }
  );
  unsubscribes.push(unsubscribeUIStateChanged);

  const cleanup = () => {
    for (const u of unsubscribes) {
      try {
        u();
      } catch {}
    }
  };
  // Listen for pan/zoom lifecycle to gate persistence
  try {
    const onPanStart = eventBus.on("stage-pan-start", () => {
      suppressPersist = true;
    });
    const onPanEnd = eventBus.on("stage-pan-end", () => {
      suppressPersist = false;
      if (pendingPersist) {
        pendingPersist = false;
        persist();
      }
    });
    const onZoomStart = eventBus.on("stage-zoom-start", () => {
      suppressPersist = true;
    });
    const onZoomEnd = eventBus.on("stage-zoom-end", () => {
      suppressPersist = false;
      if (pendingPersist) {
        pendingPersist = false;
        persist();
      }
    });
    unsubscribes.push(onPanStart, onPanEnd, onZoomStart, onZoomEnd);
  } catch {}
  try {
    const unsub = eventBus.on("app:destroy", () => cleanup());
    unsubscribes.push(unsub);
  } catch {}

  return cleanup;
};
